import {
  AXES, MAX_GUESSES,
  type AnswerEntry, type AxisState, type GuessFeedback, type Specs,
} from './types';

const ERA_EXACT = 0;
const ERA_CLOSE = 2;
const POWER_EXACT = 10;
const POWER_CLOSE = 50;
// Weight is displayed and graded in pounds (canon stores kg). 1 kg = 2.20462 lb.
const KG_TO_LB = 2.20462;
const WEIGHT_EXACT_LB = 55; // green within ~25 kg
const WEIGHT_CLOSE_LB = 200; // amber within 200 lb

function numeric(
  guess: number,
  answer: number,
  exact: number,
  close: number,
): AxisState {
  const diff = Math.abs(answer - guess);
  if (diff <= exact) return 'exact';
  // Near-miss stays amber but keeps a direction so the board can show ▲/▼.
  if (diff <= close) return answer > guess ? 'close_higher' : 'close_lower';
  return answer > guess ? 'higher' : 'lower';
}

function ordinal(guess: number, answer: number): AxisState {
  if (answer === guess) return 'exact';
  return answer > guess ? 'higher' : 'lower';
}

function cylinderState(guess: Specs, answer: Specs): AxisState {
  const gRot = guess.engine_config === 'rotary';
  const aRot = answer.engine_config === 'rotary';
  if (gRot && aRot) return 'exact';
  if (gRot || aRot) return 'wrong'; // categorical mismatch: rotary vs piston
  return ordinal(guess.cylinders as number, answer.cylinders as number);
}

function categorical(guess: string, answer: string): AxisState {
  return guess === answer ? 'match' : 'wrong';
}

/** Per-axis feedback comparing a guess's specs to the answer's specs. */
export function scoreGuess(guess: Specs, answer: Specs): GuessFeedback {
  return {
    era: numeric(guess.year, answer.year, ERA_EXACT, ERA_CLOSE),
    origin: categorical(guess.origin, answer.origin),
    layout: categorical(guess.layout, answer.layout),
    body_style: categorical(guess.body_style, answer.body_style),
    cylinders: cylinderState(guess, answer),
    aspiration: categorical(guess.aspiration, answer.aspiration),
    power: numeric(guess.power_hp, answer.power_hp, POWER_EXACT, POWER_CLOSE),
    weight: numeric(
      guess.weight_kg * KG_TO_LB,
      answer.weight_kg * KG_TO_LB,
      WEIGHT_EXACT_LB,
      WEIGHT_CLOSE_LB,
    ),
  };
}

/** Win condition is id equality, NOT all-green feedback. */
export function isSolved(guessId: string, answerId: string): boolean {
  return guessId === answerId;
}

const EMOJI: Record<AxisState, string> = {
  exact: '\u{1F7E9}', // green
  match: '\u{1F7E9}', // green
  close_higher: '\u{1F7E8}', // amber (direction carried by the on-board arrow)
  close_lower: '\u{1F7E8}', // amber
  higher: '\u{1F7E5}', // red (heat scale; arrow carries direction)
  lower: '\u{1F7E5}', // red
  wrong: '\u{1F7E5}', // red
};

/** Spoiler-free emoji grid. `solvedInN` is the guess count if solved, else 'X'. */
export function shareGrid(
  log: GuessFeedback[],
  puzzleNumber: number,
  solvedInN: number | 'X',
): string {
  const header = `DECODE #${String(puzzleNumber).padStart(3, '0')}  ${solvedInN}/${MAX_GUESSES}`;
  const rows = log.map((fb) => AXES.map((axis) => EMOJI[fb[axis]]).join(''));
  return [header, ...rows].join('\n');
}

/** Decode the base64 answer payload into the full entry (with fun_fact). */
export function decodeAnswer(b64: string): AnswerEntry {
  const json = decodeURIComponent(escape(atob(b64)));
  return JSON.parse(json) as AnswerEntry;
}
